/**
 * God-admin editable AG Grid controller for the AI Voter List page.
 *
 * Extends AgGridBaseController (shared toolkit — untouched) and adds:
 *   - Client-side row model loaded from `rowDataUrlValue`
 *   - Inline editing via AG Grid's `editable` ColDef + `onCellValueChanged`
 *   - PATCH to `updateUrlTemplateValue` (`:id` replaced with row id) on change
 *   - On success: refresh the row with the server-returned data (picks up
 *     has_error / has_warning row-class changes)
 *   - On failure: rollback cell to oldValue and surface the error
 *   - rowClassRules wired to the `row_status` field set by GridRowSerializer
 *   - `validate` action: POSTs to `validateUrlValue`, replaces full row set
 *     (empty rows may be deleted server-side), shows valid/error summary
 */

import {
  createGrid,
  GridApi,
  GridOptions,
  ColDef,
  CellValueChangedEvent,
  RowClassRules,
} from "ag-grid-enterprise";
import { AgGridBaseController } from "../../../application/toolkit/ag_grid_base_component/ag_grid_base_controller";
import { ebTheme } from "../../../application/toolkit/ag_grid_shared/eb_theme";
import { ebFilterParams, ebLocaleText } from "../../../application/toolkit/ag_grid_shared/filter_defaults";

export default class extends AgGridBaseController {
  static targets = [...AgGridBaseController.targets, "grid", "validateButton", "validateStatus", "downloadButton"];

  static values = {
    ...AgGridBaseController.values,
    rowDataUrl: String,
    updateUrlTemplate: String,
    validateUrl: String,
    downloadUrl: String,
  };

  declare readonly rowDataUrlValue: string;
  declare readonly updateUrlTemplateValue: string;
  declare readonly validateUrlValue: string;
  declare readonly hasValidateUrlValue: boolean;
  declare readonly downloadUrlValue: string;
  declare readonly hasDownloadUrlValue: boolean;

  declare readonly validateButtonTarget: HTMLButtonElement;
  declare readonly hasValidateButtonTarget: boolean;
  declare readonly validateStatusTarget: HTMLElement;
  declare readonly hasValidateStatusTarget: boolean;
  declare readonly downloadButtonTarget: HTMLElement;
  declare readonly hasDownloadButtonTarget: boolean;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  connect() {
    super.connect(); // license key + outside-click handler

    if (!this.hasGridTarget) return;

    const rowClassRules: RowClassRules = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      has_error: (params) =>
        (params.data as Record<string, unknown>)?.row_status === "error",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      has_warning: (params) =>
        (params.data as Record<string, unknown>)?.row_status === "warning",
    };

    const options: GridOptions = {
      theme: ebTheme,
      localeText: ebLocaleText,
      columnDefs: this.columnDefsValue,
      rowData: [],
      rowClassRules,
      domLayout: "autoHeight",
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        filterParams: ebFilterParams,
      },
      stopEditingWhenCellsLoseFocus: true,
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.handleCellValueChanged(event);
      },
    };

    this.gridApi = createGrid(this.gridTarget, options);

    if (this.rowDataUrlValue) {
      this.fetchRowData();
    }
  }

  disconnect() {
    super.disconnect();
  }

  // Required stubs from the abstract base (no view-toggle needed here)
  switchToBasic(): void { /* not used */ }
  switchToAdvanced(): void { /* not used */ }

  // ── Public actions ─────────────────────────────────────────────────────────

  validate(): void {
    if (!this.hasValidateUrlValue || !this.validateUrlValue) return;

    const button = this.hasValidateButtonTarget ? this.validateButtonTarget : null;
    if (button) button.disabled = true;

    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";

    fetch(this.validateUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Validate failed: ${response.status}`);
        return response.json();
      })
      .then((json) => {
        const rows = Array.isArray(json.rows) ? json.rows : [];
        // Replace the full row set — validation may have deleted empty rows
        if (this.gridApi) {
          this.gridApi.setGridOption("rowData", rows);
        }
        this.showValidateStatus(json.valid as boolean, json.error_count as number);
      })
      .catch((error) => {
        console.error("[AiVoterListGridComponent] validate error:", error);
        this.showValidateStatusError();
      })
      .finally(() => {
        if (button) button.disabled = false;
      });
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private fetchRowData(): void {
    if (!this.gridApi) return;
    this.gridApi.setGridOption("loading", true);

    fetch(this.rowDataUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
        return response.json();
      })
      .then((json) => {
        if (!this.gridApi) return;
        const rows = Array.isArray(json) ? json : (json.rows ?? []);
        this.gridApi.setGridOption("rowData", rows);
        this.gridApi.setGridOption("loading", false);
        this.autoSizeNonFlexColumns();
      })
      .catch((error) => {
        console.error("[AiVoterListGridComponent] fetchRowData error:", error);
        this.gridApi?.setGridOption("loading", false);
      });
  }

  private handleCellValueChanged(event: CellValueChangedEvent): void {
    const { colDef, newValue, oldValue, node } = event;
    const rowId = (node.data as Record<string, unknown>)?.id as string | number;
    const field = colDef.field;

    if (!field || rowId === undefined || rowId === null) return;

    const url = this.updateUrlTemplateValue.replace(":id", String(rowId));
    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";

    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
      body: JSON.stringify({ [field]: newValue }),
    })
      .then((response) => {
        return response.json().then((json) => ({ ok: response.ok, json }));
      })
      .then(({ ok, json }) => {
        if (ok && json.row) {
          // Apply refreshed row so row_status-driven CSS recolors immediately
          node.setData(json.row as Record<string, unknown>);
        } else {
          // Rollback
          node.setDataValue(field, oldValue);
          const errorMsg = this.extractError(json);
          console.warn("[AiVoterListGridComponent] update rejected:", errorMsg);
          if (errorMsg) {
            this.showRowError(node.data as Record<string, unknown>, errorMsg);
          }
        }
      })
      .catch((error) => {
        node.setDataValue(field, oldValue);
        console.error("[AiVoterListGridComponent] PATCH error:", error);
      });
  }

  private extractError(json: Record<string, unknown>): string {
    if (typeof json.errors === "string") return json.errors;
    if (json.errors && typeof json.errors === "object") {
      return Object.values(json.errors as Record<string, string[]>)
        .flat()
        .join(", ");
    }
    return "Update failed";
  }

  // Lightweight transient error surface: sets a row_status of 'error' so the
  // row flashes red. A full toast/overlay is a Delta improvement.
  private showRowError(
    rowData: Record<string, unknown>,
    _message: string
  ): void {
    if (!this.gridApi) return;
    this.gridApi.forEachNode((node) => {
      const data = node.data as Record<string, unknown>;
      if (data && data.id === rowData.id) {
        node.setData({ ...data, row_status: "error" });
      }
    });
  }

  // Uses the global `inline_success` (green) / `inline_error` (red) classes so the
  // validation message matches the organizer Setup > Voters workflow styling.
  // Also reveals the Download list button when errors are present.
  private showValidateStatus(valid: boolean, errorCount: number): void {
    if (!this.hasValidateStatusTarget) return;
    const el = this.validateStatusTarget;
    if (valid) {
      el.textContent = "Valid — no errors";
      el.className = "ai-voter-list-grid-validate-status inline_success";
      // Keep download button hidden when validation passes
      if (this.hasDownloadButtonTarget) {
        this.downloadButtonTarget.hidden = true;
      }
    } else {
      el.textContent = `${errorCount} error${errorCount === 1 ? "" : "s"} found`;
      el.className = "ai-voter-list-grid-validate-status inline_error";
      // Reveal the download button so the user can export the error list
      if (this.hasDownloadButtonTarget) {
        this.downloadButtonTarget.hidden = false;
      }
    }
  }

  private showValidateStatusError(): void {
    if (!this.hasValidateStatusTarget) return;
    const el = this.validateStatusTarget;
    el.textContent = "Validation request failed";
    el.className = "ai-voter-list-grid-validate-status inline_error";
  }
}
